import { z } from "zod";
import { addDays, daysBetween } from "./obligations.js";

/**
 * Meterstanden (docs/METERSTANDEN.md): meters als asset, opnamerondes per gebouw, verbruik,
 * plausibiliteitscheck (§5) en signalen (§6). Alles hier is puur, zodat server, web (ook offline)
 * en Excel hetzelfde rekenen. Checks en signalen worden berekend, nooit opgeslagen.
 */

export const METER_TYPES = ["electricity", "gas", "water", "heat", "cold", "other"] as const;
export type MeterType = (typeof METER_TYPES)[number];
export const METER_TYPE_LABELS: Record<MeterType, string> = {
  electricity: "Elektra",
  gas: "Gas",
  water: "Water",
  heat: "Warmte",
  cold: "Koude",
  other: "Overig",
};
export const METER_DEFAULT_UNITS: Record<MeterType, string> = {
  electricity: "kWh",
  gas: "m³",
  water: "m³",
  heat: "GJ",
  cold: "GJ",
  other: "",
};
export const METER_UNIT_SUGGESTIONS = ["kWh", "MWh", "m³", "GJ", "liter"] as const;

export const REGISTER_DIRECTIONS = ["consumption", "production"] as const;
export type RegisterDirection = (typeof REGISTER_DIRECTIONS)[number];
export const REGISTER_DIRECTION_LABELS: Record<RegisterDirection, string> = {
  consumption: "Verbruik",
  production: "Teruglevering",
};

export const READING_EVENTS = ["normal", "replaced", "rollover"] as const;
export type ReadingEvent = (typeof READING_EVENTS)[number];
export const READING_EVENT_LABELS: Record<ReadingEvent, string> = {
  normal: "Normaal",
  replaced: "Meterwissel",
  rollover: "Rondgetikt",
};

export const METER_FREQUENCIES = ["monthly", "quarterly", "yearly"] as const;
export type MeterFrequency = (typeof METER_FREQUENCIES)[number];
export const METER_FREQUENCY_LABELS: Record<MeterFrequency, string> = {
  monthly: "Per maand",
  quarterly: "Per kwartaal",
  yearly: "Per jaar",
};
export const DEFAULT_METER_REMINDER_DAYS = 7;

export const METER_ROUND_STATUSES = ["open", "completed"] as const;
export type MeterRoundStatus = (typeof METER_ROUND_STATUSES)[number];
export const METER_ROUND_STATUS_LABELS: Record<MeterRoundStatus, string> = {
  open: "Open",
  completed: "Afgerond",
};

// ── Perioden ──

const MONTHS = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];
const pad = (n: number) => String(n).padStart(2, "0");
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** Periode (kalendermaand, -kwartaal of -jaar) waarin `date` valt. */
export function periodOf(frequency: MeterFrequency, date: string): { start: string; end: string; name: string } {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7));
  if (frequency === "monthly")
    return {
      start: `${y}-${pad(m)}-01`,
      end: `${y}-${pad(m)}-${pad(lastDay(y, m))}`,
      name: `Meterstanden ${MONTHS[m - 1]} ${y}`,
    };
  if (frequency === "quarterly") {
    const q = Math.ceil(m / 3);
    const last = q * 3;
    return {
      start: `${y}-${pad(last - 2)}-01`,
      end: `${y}-${pad(last)}-${pad(lastDay(y, last))}`,
      name: `Meterstanden Q${q} ${y}`,
    };
  }
  return { start: `${y}-01-01`, end: `${y}-12-31`, name: `Meterstanden ${y}` };
}

/** Naam van een handmatige ronde: die van de periode als het precies een maand/kwartaal/jaar is. */
export function roundName(start: string, end: string): string {
  for (const f of METER_FREQUENCIES) {
    const p = periodOf(f, start);
    if (p.start === start && p.end === end) return p.name;
  }
  const fmt = (iso: string) => `${Number(iso.slice(8, 10))} ${MONTHS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;
  return `Meterstanden ${fmt(start)} t/m ${fmt(end)}`;
}

// ── Verbruik ──

export interface ReadingValueInput {
  registerId: string;
  value: number;
  /** Meterwissel: eindstand van de oude meter. */
  oldFinal?: number | null;
  /** Meterwissel: beginstand van de nieuwe meter. */
  newStart?: number | null;
  /** Extra foto van dit telwerk (optioneel; de foto van de meter is verplicht). */
  photoFileId?: string | null;
}

/** Eén opname zoals de berekeningen hem nodig hebben (ronde of import). */
export interface ReadingPoint {
  id: string;
  readOn: string;
  unreachable: boolean;
  event: ReadingEvent;
  values: ReadingValueInput[];
}

export interface MeterConfig {
  /** Voor de seizoensverwachting (gas, warmte) en de eenheid in meldingen. */
  meterType?: MeterType;
  unit?: string;
  digits: number | null;
  factor: number;
  baseLoadPerDay: number | null;
  registers: { id: string; name: string; direction: RegisterDirection }[];
}

const round3 = (n: number) => Math.round(n * 1000) / 1000;

/**
 * Verbruik van één telwerk tussen twee standen (§4): normaal, rondgetikt of meterwissel.
 * null als het niet te berekenen is (stand lager zonder gebeurtenis, of wissel zonder standen).
 */
export function registerUsage(
  prev: number,
  cur: { value: number; event: ReadingEvent; oldFinal?: number | null; newStart?: number | null },
  meter: Pick<MeterConfig, "digits" | "factor">,
): number | null {
  let raw: number;
  if (cur.event === "replaced") {
    if (cur.oldFinal == null || cur.newStart == null) return null;
    raw = cur.oldFinal - prev + (cur.value - cur.newStart);
  } else if (cur.event === "rollover") {
    if (!meter.digits) return null;
    raw = 10 ** meter.digits - prev + cur.value;
  } else raw = cur.value - prev;
  if (raw < 0) return null;
  return round3(raw * (meter.factor || 1));
}

export interface UsagePeriod {
  /** Opname aan het einde van de periode. */
  readingId: string;
  from: string;
  to: string;
  days: number;
  /** Verbruik per telwerk; null = niet te berekenen. */
  registers: Record<string, number | null>;
  /** Som van de verbruik-telwerken; null als een ervan ontbreekt. */
  consumption: number | null;
  production: number | null;
  perDay: number | null;
}

/** Perioden tussen opeenvolgende bereikbare opnames (oudste eerst). */
export function usagePeriods(meter: MeterConfig, readings: readonly ReadingPoint[]): UsagePeriod[] {
  const list = readings.filter((r) => !r.unreachable).sort((a, b) => a.readOn.localeCompare(b.readOn));
  const out: UsagePeriod[] = [];
  for (let i = 1; i < list.length; i++) {
    const a = list[i - 1]!;
    const b = list[i]!;
    const days = daysBetween(a.readOn, b.readOn);
    if (days <= 0) continue;
    const registers: Record<string, number | null> = {};
    let consumption: number | null = 0;
    let production: number | null = 0;
    let hasProduction = false;
    for (const reg of meter.registers) {
      const pv = a.values.find((v) => v.registerId === reg.id);
      const cv = b.values.find((v) => v.registerId === reg.id);
      const u = pv && cv ? registerUsage(pv.value, { ...cv, event: b.event }, meter) : null;
      registers[reg.id] = u;
      if (reg.direction === "production") {
        hasProduction = true;
        production = u == null || production == null ? null : round3(production + u);
      } else consumption = u == null || consumption == null ? null : round3(consumption + u);
    }
    out.push({
      readingId: b.id,
      from: a.readOn,
      to: b.readOn,
      days,
      registers,
      consumption,
      production: hasProduction ? production : null,
      perDay: consumption == null ? null : consumption / days,
    });
  }
  return out;
}

/** Soorten met een stookseizoen: de verwachting komt uit dezelfde periode een jaar eerder. */
export const SEASONAL_METER_TYPES: readonly MeterType[] = ["gas", "heat"];
export const isSeasonal = (meter: Pick<MeterConfig, "meterType">) =>
  meter.meterType != null && SEASONAL_METER_TYPES.includes(meter.meterType);

/** Gemiddeld verbruik per dag van een telwerk over de laatste `n` perioden; null zonder historie. */
export function expectedPerDay(periods: readonly UsagePeriod[], registerId: string, n = 4): number | null {
  const vals = periods
    .slice(-n)
    .map((p) => (p.registers[registerId] == null ? null : p.registers[registerId]! / p.days))
    .filter((v): v is number => v != null);
  if (!vals.length) return null;
  return vals.reduce((s, v) => s + v, 0) / vals.length;
}

// ── Leegstand ──

export interface VacancyPeriod {
  /** null = het hele gebouw. */
  assetId: string | null;
  startsOn: string;
  endsOn: string | null;
}

/** Valt de periode `from`–`to` helemaal binnen leegstand die voor deze meter geldt? */
export function isVacant(
  vacancies: readonly VacancyPeriod[],
  meter: { assetId: string; parentAssetId: string | null },
  from: string,
  to: string,
): boolean {
  return vacancies.some(
    (v) =>
      (v.assetId === null || v.assetId === meter.assetId) &&
      v.startsOn <= from &&
      (v.endsOn === null || v.endsOn >= to),
  );
}

/** Staat de meter op `date` leeg (voor de weergave)? */
export const vacantOn = (
  vacancies: readonly VacancyPeriod[],
  meter: { assetId: string; parentAssetId: string | null },
  date: string,
) => isVacant(vacancies, meter, date, date);

/** Toegestaan verbruik bij leegstand: basislast × dagen, met 10% en 1 eenheid marge. */
const vacancyAllowance = (baseLoadPerDay: number | null, days: number) => (baseLoadPerDay ?? 0) * days * 1.1 + 1;

// ── Plausibiliteitscheck (§5) ──

export const CHECK_CODES = ["lower", "decimal", "high", "low", "zero", "vacant", "children", "missing"] as const;
export type CheckCode = (typeof CHECK_CODES)[number];
export type CheckSeverity = "error" | "warning";

export interface CheckIssue {
  code: CheckCode;
  severity: CheckSeverity;
  message: string;
  registerId?: string;
  /** Kommafout: de getypte stand met de komma verschoven (nooit een berekend getal). */
  suggestion?: number;
}

export interface ReadingCheck {
  issues: CheckIssue[];
  /** Verbruik per telwerk sinds de vorige bereikbare opname. */
  usage: Record<string, number | null>;
  /** Verwacht verbruik per telwerk over dezelfde dagen; null zonder historie. */
  expected: Record<string, number | null>;
  previous: ReadingPoint | null;
  days: number | null;
}

const nl = (n: number) => n.toLocaleString("nl-NL", { maximumFractionDigits: 3 });
/** Hoeveelheid voor in een melding: minder decimalen naarmate het getal groter is. */
const qty = (n: number) =>
  n.toLocaleString("nl-NL", { maximumFractionDigits: Math.abs(n) >= 100 ? 0 : Math.abs(n) >= 10 ? 1 : 2 });
const withUnit = (n: number, unit?: string) => (unit ? `${qty(n)} ${unit}` : qty(n));
const times = (r: number) => `${r.toLocaleString("nl-NL", { maximumFractionDigits: 1 })}×`;
/** Melding met (bij meer telwerken) de naam ervoor: "Dal: verbruik …" of "Verbruik …". */
const msg = (label: string, text: string) => (label ? label + text : text[0]!.toUpperCase() + text.slice(1));

/**
 * De getypte stand met de komma 1 tot 3 plaatsen verschoven, als dat een plausibel verbruik geeft
 * (tussen 1/3 en 3× het verwachte). Het dichtst bij het verwachte wint. Zo stelt de app alleen een
 * getal voor dat op de meter kan staan, geen berekende stand.
 */
function commaVariant(value: number, prev: number, exp: number, factor: number): number | undefined {
  let best: number | undefined;
  let bestScore = Infinity;
  for (const f of [10, 100, 1000, 0.1, 0.01, 0.001]) {
    const s = round3(value * f);
    const r = ((s - prev) * factor) / exp;
    if (!(r >= 1 / 3 && r <= 3)) continue;
    const score = Math.abs(Math.log(r));
    if (score < bestScore) {
      best = s;
      bestScore = score;
    }
  }
  return best;
}

/**
 * Verwacht verbruik per dag voor de periode `from`–`to`. Gas en warmte volgen het seizoen: dan
 * telt dezelfde periode een jaar eerder, als die er is. Anders het gemiddelde van de laatste perioden.
 */
export function expectedPerDayFor(
  meter: Pick<MeterConfig, "meterType">,
  periods: readonly UsagePeriod[],
  registerId: string,
  from: string,
  to: string,
): number | null {
  if (isSeasonal(meter)) {
    const days = daysBetween(from, to);
    const probe = { readingId: "", from, to, days, registers: {}, consumption: null, production: null, perDay: null };
    const y = days > 0 ? yearBefore(periods, probe) : null;
    const v = y?.registers[registerId];
    if (y && v != null) return v / y.days;
  }
  return expectedPerDay(periods, registerId);
}

/**
 * Controle van een nieuwe opname tegen de historie van de meter (opnames vóór deze, in willekeurige
 * volgorde). Fouten blokkeren opslaan; waarschuwingen vragen een toelichting.
 */
export function checkReading(
  meter: MeterConfig & { assetId: string; parentAssetId: string | null },
  history: readonly ReadingPoint[],
  reading: Omit<ReadingPoint, "id"> & { id?: string },
  vacancies: readonly VacancyPeriod[] = [],
): ReadingCheck {
  const usage: Record<string, number | null> = {};
  const expected: Record<string, number | null> = {};
  const issues: CheckIssue[] = [];
  const empty = { issues, usage, expected, previous: null, days: null };
  if (reading.unreachable) return empty;
  for (const reg of meter.registers)
    if (!reading.values.some((v) => v.registerId === reg.id && Number.isFinite(v.value)))
      issues.push({
        code: "missing",
        severity: "error",
        registerId: reg.id,
        message: `Vul de stand van ${reg.name} in`,
      });
  const before = history
    .filter((r) => !r.unreachable && r.readOn <= reading.readOn && r.id !== reading.id)
    .sort((a, b) => a.readOn.localeCompare(b.readOn));
  const previous = before.at(-1) ?? null;
  if (!previous) return { ...empty, issues };
  const days = daysBetween(previous.readOn, reading.readOn);
  const periods = usagePeriods(meter, before);
  let total = 0;
  let totalKnown = true;
  for (const reg of meter.registers) {
    const pv = previous.values.find((v) => v.registerId === reg.id);
    const cv = reading.values.find((v) => v.registerId === reg.id);
    if (!pv || !cv || !Number.isFinite(cv.value)) {
      usage[reg.id] = null;
      expected[reg.id] = null;
      totalKnown = false;
      continue;
    }
    const u = registerUsage(pv.value, { ...cv, event: reading.event }, meter);
    usage[reg.id] = u;
    const perDay = expectedPerDayFor(meter, periods, reg.id, previous.readOn, reading.readOn);
    const exp = perDay == null || days <= 0 ? null : perDay * days;
    expected[reg.id] = exp;
    const label = meter.registers.length > 1 ? `${reg.name}: ` : "";
    const unit = meter.unit || undefined;
    const variant =
      reading.event === "normal" && exp != null && exp > 0
        ? commaVariant(cv.value, pv.value, exp, meter.factor || 1)
        : undefined;
    if (u == null) {
      if (reading.event === "replaced")
        issues.push({
          code: "missing",
          severity: "error",
          registerId: reg.id,
          message: msg(
            label,
            "vul bij een meterwissel de eindstand van de oude en de beginstand van de nieuwe meter in",
          ),
        });
      else if (reading.event === "rollover")
        issues.push({
          code: "missing",
          severity: "error",
          registerId: reg.id,
          message: msg(label, "stel eerst het aantal cijfers van de meter in"),
        });
      else
        issues.push({
          code: "lower",
          severity: "error",
          registerId: reg.id,
          suggestion: variant,
          message: msg(
            label,
            variant != null
              ? `stand ${nl(cv.value)} is lager dan de vorige (${nl(pv.value)}). Staat de komma goed? Anders Meterwissel of Rondgetikt kiezen.`
              : `stand ${nl(cv.value)} is lager dan de vorige (${nl(pv.value)}). Kies Meterwissel of Rondgetikt als dat zo is.`,
          ),
        });
      totalKnown = false;
      continue;
    }
    if (reg.direction === "consumption") total += u;
    if (exp == null || exp <= 0) continue;
    const ratio = u / exp;
    const near = (r: number, t: number) => r >= t * 0.65 && r <= t * 1.35;
    const decimal = [10, 100, 0.1, 0.01].find((t) => near(ratio, t));
    const off = ratio > 3 || ratio < 1 / 3;
    if (reading.event === "normal" && (decimal || (variant != null && off))) {
      const r = decimal ?? ratio;
      const rText = r >= 1 ? (decimal ? `ongeveer ${decimal}×` : times(r)) : `ongeveer 1/${Math.round(1 / r)}`;
      issues.push({
        code: "decimal",
        severity: "warning",
        registerId: reg.id,
        suggestion: variant,
        message: msg(
          label,
          `verbruik ${withUnit(u, unit)} is ${rText} het verwachte (${withUnit(exp, unit)}). Staat de komma goed? ${
            variant != null
              ? `Met de komma verschoven staat er ${nl(variant)}.`
              : "Lees de meter nog eens af en vergelijk met de foto."
          }`,
        ),
      });
    } else if (u === 0)
      issues.push({
        code: "zero",
        severity: "warning",
        registerId: reg.id,
        message: msg(
          label,
          `geen verbruik, terwijl er normaal ${withUnit(exp, unit)} wordt verbruikt. Staat de meter stil?`,
        ),
      });
    else if (ratio > 3)
      issues.push({
        code: "high",
        severity: "warning",
        registerId: reg.id,
        message: msg(label, `verbruik ${withUnit(u, unit)} is ${times(ratio)} het verwachte (${withUnit(exp, unit)})`),
      });
    else if (ratio < 1 / 3)
      issues.push({
        code: "low",
        severity: "warning",
        registerId: reg.id,
        message: msg(label, `verbruik ${withUnit(u, unit)} is veel lager dan verwacht (${withUnit(exp, unit)})`),
      });
  }
  if (
    totalKnown &&
    days > 0 &&
    isVacant(vacancies, meter, previous.readOn, reading.readOn) &&
    total > vacancyAllowance(meter.baseLoadPerDay, days)
  )
    issues.push({
      code: "vacant",
      severity: "warning",
      message: `Verbruik ${withUnit(total, meter.unit)} terwijl het pand leeg staat${meter.baseLoadPerDay ? ` (basislast ${withUnit(meter.baseLoadPerDay * days, meter.unit)})` : ""}. Lekkage of ongewenst gebruik?`,
    });
  return { issues, usage, expected, previous, days };
}

/** Een opname met waarschuwingen mag alleen met toelichting worden bewaard. */
export const needsRemark = (c: Pick<ReadingCheck, "issues">) => c.issues.some((i) => i.severity === "warning");
export const hasErrors = (c: Pick<ReadingCheck, "issues">) => c.issues.some((i) => i.severity === "error");

/** Tussenmeters samen meer dan de hoofdmeter (+5% marge en 1 eenheid). null = in orde of onbekend. */
export function childrenCheck(main: number | null, children: readonly (number | null)[]) {
  if (main == null || !children.length || children.some((c) => c == null)) return null;
  const sum = round3(children.reduce<number>((s, c) => s + c!, 0));
  return {
    sum,
    unexplained: round3(main - sum),
    exceeds: sum > main * 1.05 + 1,
  };
}

// ── Signalen (§6) ──

export const SIGNAL_CODES = ["jump", "creep", "vacant", "stuck"] as const;
export type SignalCode = (typeof SIGNAL_CODES)[number];
export const SIGNAL_LABELS: Record<SignalCode, string> = {
  jump: "Sprong in verbruik",
  creep: "Sluipende stijging",
  vacant: "Verbruik bij leegstand",
  stuck: "Meter staat stil",
};

export interface MeterSignal {
  code: SignalCode;
  /** Opname aan het einde van de periode waar het signaal over gaat. */
  readingId: string;
  to: string;
  message: string;
}

const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
};
const mid = (p: UsagePeriod) => addDays(p.from, Math.round(p.days / 2));
const pctText = (r: number) => `${r >= 1 ? "+" : ""}${Math.round((r - 1) * 100)}%`;

/**
 * Periode van ongeveer een jaar eerder: midden binnen een halve periode (minimaal 7, hoogstens 45 dagen)
 * van 365 dagen terug, zodat een maand alleen met dezelfde maand vorig jaar paart.
 */
export function yearBefore(periods: readonly UsagePeriod[], p: UsagePeriod): UsagePeriod | null {
  const target = addDays(mid(p), -365);
  let best: UsagePeriod | null = null;
  let bestDist = Math.min(45, Math.max(7, Math.round(p.days / 2))) + 1;
  for (const q of periods) {
    if (q === p) continue;
    const d = Math.abs(daysBetween(target, mid(q)));
    if (d < bestDist) {
      best = q;
      bestDist = d;
    }
  }
  return best;
}

/** Signalen over de laatste periode van een meter (de reeks van alle bereikbare opnames). */
export function meterSignals(
  meter: MeterConfig & { assetId: string; parentAssetId: string | null },
  readings: readonly ReadingPoint[],
  vacancies: readonly VacancyPeriod[] = [],
): MeterSignal[] {
  const periods = usagePeriods(meter, readings);
  const last = periods.at(-1);
  if (!last || last.perDay == null) return [];
  const out: MeterSignal[] = [];
  const base = { readingId: last.readingId, to: last.to };
  if (
    isVacant(vacancies, meter, last.from, last.to) &&
    last.consumption! > vacancyAllowance(meter.baseLoadPerDay, last.days)
  )
    out.push({
      ...base,
      code: "vacant",
      message: `${withUnit(last.consumption!, meter.unit)} verbruikt in ${last.days} dagen leegstand`,
    });
  // Gas en warmte: vergelijken met dezelfde periode een jaar eerder (stookseizoen), anders met de mediaan.
  const lastYear = isSeasonal(meter) ? yearBefore(periods, last)?.perDay : null;
  const earlier = periods
    .slice(0, -1)
    .slice(-6)
    .map((p) => p.perDay)
    .filter((v): v is number => v != null);
  const ref = lastYear ? { value: lastYear, text: "dezelfde periode vorig jaar" } : null;
  const m = ref?.value ?? (earlier.length >= 3 ? median(earlier) : null);
  if (m != null && m > 0 && (last.perDay > 2 * m || last.perDay < m / 2))
    out.push({
      ...base,
      code: "jump",
      message: `${withUnit(last.perDay, meter.unit)} per dag tegen ${ref ? "" : "gemiddeld "}${withUnit(m, meter.unit)}${ref ? ` ${ref.text}` : ""} (${pctText(last.perDay / m)})`,
    });
  const recent = periods.slice(-3);
  if (recent.length === 3) {
    const ratios = recent.map((p) => {
      const y = yearBefore(periods, p);
      return p.perDay != null && y?.perDay ? p.perDay / y.perDay : null;
    });
    if (ratios.every((r) => r != null && r > 1.2))
      out.push({
        ...base,
        code: "creep",
        message: `Drie perioden achter elkaar hoger dan een jaar eerder (${ratios.map((r) => pctText(r!)).join(", ")})`,
      });
    const before = periods.slice(0, -3);
    if (
      recent.every((p) => p.consumption === 0) &&
      before.some((p) => (p.consumption ?? 0) > 0) &&
      !isVacant(vacancies, meter, recent[0]!.from, last.to)
    )
      out.push({ ...base, code: "stuck", message: "Drie opnames achter elkaar zonder verbruik" });
  }
  return out;
}

// ── API ──

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Datum als JJJJ-MM-DD");
const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => v || null);
const reading = z.number().min(0).max(1e11);

export const meterRegisterSchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1, "Geef het telwerk een naam").max(60),
  direction: z.enum(REGISTER_DIRECTIONS).default("consumption"),
});

export const meterUpsertSchema = z.object({
  meterType: z.enum(METER_TYPES),
  unit: z.string().trim().max(20).default(""),
  ean: z
    .string()
    .trim()
    .regex(/^\d{18}$/, "Een EAN-code heeft 18 cijfers")
    .nullish()
    .or(z.literal("").transform(() => null)),
  meterNumber: optText(60),
  digits: z.number().int().min(1).max(12).nullish(),
  factor: z.number().positive().max(1_000_000).default(1),
  parentAssetId: z.uuid().nullish(),
  baseLoadPerDay: z.number().min(0).max(1e9).nullish(),
  active: z.boolean().default(true),
  registers: z.array(meterRegisterSchema).min(1, "Een meter heeft minstens één telwerk").max(8),
});
export type MeterUpsert = z.input<typeof meterUpsertSchema>;

export const vacancyCreateSchema = z
  .object({
    id: z.uuid(),
    /** Leeg = het hele gebouw. */
    assetId: z.uuid().nullish(),
    startsOn: isoDate,
    endsOn: isoDate.nullish(),
    note: optText(300),
  })
  .refine((v) => !v.endsOn || v.endsOn >= v.startsOn, { message: "Het einde ligt voor het begin", path: ["endsOn"] });
export const vacancyUpdateSchema = z
  .object({ startsOn: isoDate, endsOn: isoDate.nullable(), note: optText(300) })
  .partial()
  .refine((v) => !v.endsOn || !v.startsOn || v.endsOn >= v.startsOn, {
    message: "Het einde ligt voor het begin",
    path: ["endsOn"],
  });

export const meterScheduleSchema = z.object({
  frequency: z.enum(METER_FREQUENCIES),
  startsOn: isoDate,
  reminderDays: z.number().int().min(0).max(90).default(DEFAULT_METER_REMINDER_DAYS),
  active: z.boolean().default(true),
});
export type MeterScheduleBody = z.input<typeof meterScheduleSchema>;

export const meterRoundCreateSchema = z
  .object({
    id: z.uuid(),
    periodStart: isoDate,
    periodEnd: isoDate,
    name: optText(120),
  })
  .refine((v) => v.periodEnd >= v.periodStart, { message: "Het einde ligt voor het begin", path: ["periodEnd"] });

export const meterReadingSchema = z
  .object({
    id: z.uuid(),
    assetId: z.uuid(),
    readOn: isoDate,
    unreachable: z.boolean().default(false),
    unreachableReason: optText(300),
    photoFileId: z.uuid().nullish(),
    event: z.enum(READING_EVENTS).default("normal"),
    remark: optText(1000),
    values: z
      .array(
        z.object({
          registerId: z.uuid(),
          value: reading,
          oldFinal: reading.nullish(),
          newStart: reading.nullish(),
          photoFileId: z.uuid().nullish(),
        }),
      )
      .max(8)
      .default([]),
  })
  .refine((r) => !r.unreachable || Boolean(r.unreachableReason), {
    message: "Geef aan waarom de meter niet bereikbaar is",
    path: ["unreachableReason"],
  })
  .refine((r) => r.unreachable || Boolean(r.photoFileId), {
    message: "Maak een foto van de meter",
    path: ["photoFileId"],
  });
export type MeterReadingBody = z.input<typeof meterReadingSchema>;

/** Toelichting en foto mogen ook na afronden nog. */
export const meterReadingUpdateSchema = z.object({ remark: optText(1000).optional() });

// ── DTO's ──

export interface MeterRegisterDto {
  id: string;
  name: string;
  direction: RegisterDirection;
  sort: number;
}

export interface MeterDto {
  assetId: string;
  meterType: MeterType;
  unit: string;
  ean: string | null;
  meterNumber: string | null;
  digits: number | null;
  factor: number;
  parentAssetId: string | null;
  baseLoadPerDay: number | null;
  active: boolean;
  registers: MeterRegisterDto[];
}

export interface MeterReadingDto extends ReadingPoint {
  assetId: string;
  roundId: string | null;
  unreachableReason: string | null;
  photoFileId: string | null;
  remark: string | null;
  findingId: string | null;
  findingCode: string | null;
  source: "round" | "import";
  createdBy: string | null;
  createdAt: string;
}

export interface VacancyDto extends VacancyPeriod {
  id: string;
  buildingId: string;
  note: string | null;
}

export interface MeterScheduleDto {
  buildingId: string;
  frequency: MeterFrequency;
  startsOn: string;
  reminderDays: number;
  active: boolean;
}

export interface MeterRoundDto {
  id: string;
  buildingId: string;
  periodStart: string;
  periodEnd: string;
  name: string;
  status: MeterRoundStatus;
  completedAt: string | null;
  total: number;
  done: number;
  unreachable: number;
  /** Opnames met waarschuwingen (bevestigd met toelichting). */
  flagged: number;
}

export interface RoundMeterAsset {
  id: string;
  code: string;
  name: string;
  floorId: string | null;
  floorName: string | null;
  roomName: string | null;
  qrCode: string | null;
}

/** Eén meter in een ronde, met de historie die de veldflow (offline) nodig heeft voor de check. */
export interface RoundMeterDto {
  asset: RoundMeterAsset;
  meter: MeterDto;
  /** Bereikbare opnames vóór deze ronde, nieuwste laatst (maximaal 16, genoeg voor een jaar eerder). */
  history: ReadingPoint[];
  reading: MeterReadingDto | null;
  check: ReadingCheck | null;
  vacantNow: boolean;
}

export interface RoundChildrenCheck {
  main: { id: string; code: string; name: string };
  mainUsage: number | null;
  sum: number;
  unexplained: number;
  exceeds: boolean;
  unit: string;
}

export interface MeterRoundDetailDto {
  round: MeterRoundDto;
  building: { id: string; code: string; name: string };
  meters: RoundMeterDto[];
  vacancies: VacancyDto[];
  children: RoundChildrenCheck[];
}

/** Blok *Meter* in het assetpaneel. */
export interface AssetMeterDto {
  meter: MeterDto | null;
  readings: MeterReadingDto[];
  periods: (UsagePeriod & { lastYearPerDay: number | null })[];
  signals: MeterSignal[];
  vacancies: VacancyDto[];
  vacantNow: boolean;
  parent: { id: string; code: string; name: string } | null;
  children: { id: string; code: string; name: string }[];
}

export interface MeterSignalRow extends MeterSignal {
  building: { id: string; code: string; name: string };
  asset: { id: string; code: string; name: string };
  meterType: MeterType;
  findingId: string | null;
}

export interface MeterCompareRow {
  building: { id: string; code: string; name: string };
  meterType: MeterType;
  unit: string;
  meters: number;
  /** Verbruik in de gekozen periode (perioden die er helemaal in vallen), per dag. */
  perDay: number | null;
  usage: number | null;
  days: number;
  /** Per dag een jaar eerder, alleen over perioden die in beide jaren bestaan. */
  lastYearPerDay: number | null;
  /** Per dag nu over diezelfde (gepaarde) perioden; hiermee vergelijk je met lastYearPerDay. */
  comparedPerDay: number | null;
  comparedDays: number;
}

export interface MeterImportResult {
  imported: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

/** Eén telwerk van een meter in een ronde, voor de tabel op de rondepagina en het PDF-rapport. */
export interface RoundRegisterRow {
  registerId: string;
  registerName: string;
  previousOn: string | null;
  previous: number | null;
  readOn: string | null;
  value: number | null;
  usage: number | null;
  days: number | null;
  expected: number | null;
  /** Verbruik per dag in deze ronde en in dezelfde periode een jaar eerder. */
  perDay: number | null;
  lastYearPerDay: number | null;
}

/** Per telwerk de vorige en nieuwe stand, het verbruik en de vergelijking met een jaar eerder. */
export function roundRegisterRows(m: RoundMeterDto): RoundRegisterRow[] {
  const cfg = { ...m.meter, assetId: m.asset.id };
  const r = m.reading && !m.reading.unreachable ? m.reading : null;
  const prev = m.check?.previous ?? m.history.at(-1) ?? null;
  const periods = r ? usagePeriods(cfg, [...m.history, r]) : [];
  const cur = r ? periods.find((p) => p.readingId === r.id) : undefined;
  const ly = cur ? yearBefore(periods, cur) : null;
  return m.meter.registers.map((reg) => {
    const usage = m.check?.usage[reg.id] ?? null;
    const days = m.check?.days ?? null;
    const lyUsage = ly?.registers[reg.id];
    return {
      registerId: reg.id,
      registerName: reg.name,
      previousOn: prev?.readOn ?? null,
      previous: prev?.values.find((v) => v.registerId === reg.id)?.value ?? null,
      readOn: r?.readOn ?? null,
      value: r?.values.find((v) => v.registerId === reg.id)?.value ?? null,
      usage,
      days,
      expected: m.check?.expected[reg.id] ?? null,
      perDay: usage != null && days ? usage / days : null,
      lastYearPerDay: ly && lyUsage != null ? lyUsage / ly.days : null,
    };
  });
}
